import { createContext } from "react";

/**
 * The render modes the app can run in. Use these constants instead of the raw
 * strings so consumers compare against a single source of truth.
 *
 *   - LIVE    — patient app (default). All hooks call the API as normal.
 *   - PREVIEW — embedded by the Designer for live preview. No network I/O.
 *               File/drawing-pad hooks return null/no-op. QuestModel arrives
 *               only via postMessage; nothing is POSTed back.
 *   - PRINT   — bundled into a self-contained HTML doc by the Designer Worker
 *               and rendered headlessly. Same no-network rules as PREVIEW.
 *
 * See docs/features/2-preview-route.md.
 */
export const RenderMode = Object.freeze({
  LIVE: "live",
  PREVIEW: "preview",
  PRINT: "print",
});

/**
 * True only in the live patient flow — the one mode with a save path and
 * network I/O. Preview and print are both read-only/no-network, so predicates
 * should key off "is live" rather than enumerating every non-live mode; that
 * way a new mode inherits the safe default without touching each call site.
 */
export const isLiveMode = (mode) => mode === RenderMode.LIVE;

/**
 * Render-mode context consumed by widgets and hooks that must behave
 * differently in the preview/print paths than in the live patient flow.
 * The value is `{ mode: RenderMode }`, defaulting to LIVE outside a provider.
 */
export const PreviewModeContext = createContext({ mode: RenderMode.LIVE });
